import React from "react";
import { Quote, Star } from "lucide-react";

/**
 * Alumni testimonials — 3 stories, bilingual (HE + AR).
 * Placeholder content — user can edit later via admin panel or hardcoded update.
 */
const TESTIMONIALS = [
    {
        id: 1,
        nameHe: "סמאח (סיעוד)",
        nameAr: "سماح (تمريض)",
        // Warm portrait, north-Israel feel (Unsplash free-license)
        photo: "https://images.unsplash.com/photo-1580489944761-15a19d654956?auto=format&fit=crop&w=400&q=80",
        roleHe: "אחות מוסמכת · בית חולים העמק",
        roleAr: "ممرضة مسجّلة · مستشفى العفولة",
        quoteHe: "התחלתי בבגרות של 78 בלי לדעת מה אני רוצה. בשנתיים סיימתי את התואר הראשון, ואחריו את ההסבה לסיעוד עם מלגה מלאה. היום אני עובדת במקצוע שאני אוהבת.",
        quoteAr: "بدأت ببجروت 78 دون معرفة ماذا أريد. خلال سنتين أنهيت اللقب الأول، ثم تأهيل التمريض بمنحة كاملة. اليوم أعمل في المهنة التي أحبها.",
        year: "2023",
    },
    {
        id: 2,
        nameHe: "אחמד (M.Teach)",
        nameAr: "أحمد (M.Teach)",
        photo: "https://images.unsplash.com/photo-1618077360395-f3068be8e001?auto=format&fit=crop&w=400&q=80",
        roleHe: "מורה במגמת מדעים · תיכון עמאל",
        roleAr: "معلم علوم · ثانوية عمال",
        quoteHe: "המלצה לכל מי שרוצה להיות מורה — התואר של אל-אהלייה + M.Teach ממומן על ידי משרד החינוך. חסכתי 60,000 ₪ ועכשיו יש לי תואר שני.",
        quoteAr: "أنصح كل من يريد أن يكون معلماً — لقب الأهلية + M.Teach ممول من قبل وزارة التربية. وفّرت 60,000 شيكل ولديّ الآن لقب ثاني.",
        year: "2024",
    },
    {
        id: 3,
        nameHe: "נור (רנטגן)",
        nameAr: "نور (أشعة)",
        photo: "https://images.unsplash.com/photo-1594824388863-c8cfb02b2cba?auto=format&fit=crop&w=400&q=80",
        roleHe: "טכנאית הדמיה · מכבי שירותי בריאות",
        roleAr: "فنية تصوير طبي · مكابي خدمات صحية",
        quoteHe: "היחס האישי במכללה עשה את כל ההבדל. המרצים מכירים אותך בשם, עוזרים אחרי השיעורים, ומלווים ברישום להמשך. שנתיים שעברו מהר.",
        quoteAr: "المرافقة الشخصية في الكلية أحدثت الفرق كله. المحاضرون يعرفونك بالاسم، يساعدون بعد الدروس، ويرافقون بالتسجيل للمتابعة. سنتان مرّتا بسرعة.",
        year: "2023",
    },
];

export default function Testimonials({ lang }) {
    const isAr = lang === "ar";

    return (
        <section className="mb-16" data-testid="testimonials-section">
            {/* Header */}
            <div className="text-center mb-10">
                <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#0B1F3D]/8 border border-[#0B1F3D]/20 text-[#0B1F3D] text-[11px] font-bold uppercase tracking-[0.2em] mb-3">
                    {isAr ? "قصص نجاح" : "סיפורי הצלחה"}
                </div>
                <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#0B1F3D]">
                    {isAr ? "بوجوه بوجهت مثلك — ونجحوا" : "פנים שהיו במקום שלך — והצליחו"}
                </h2>
                <p className="text-[#475569] mt-2 max-w-xl mx-auto text-sm">
                    {isAr
                        ? "بوجه شخصية حقيقية من خرّيجينا — أعرف لماذا يختارون الأهلية."
                        : "בוגרים אמיתיים משתפים — למה בחרו באל-אהלייה, ולאן זה לקח אותם."}
                </p>
            </div>

            {/* 3-column grid on desktop, stack on mobile */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {TESTIMONIALS.map((t) => (
                    <article
                        key={t.id}
                        data-testid={`testimonial-${t.id}`}
                        className="relative bg-white rounded-2xl p-6 border border-[#F1E4D2] shadow-[0_6px_20px_rgba(11,31,61,0.06)] hover:shadow-[0_14px_32px_rgba(212,118,31,0.18)] hover:-translate-y-1 transition-all duration-300"
                    >
                        {/* Decorative quote */}
                        <Quote size={36} className="absolute top-4 end-4 text-[#F1E4D2]" />

                        {/* Star rating */}
                        <div className="flex gap-0.5 mb-3">
                            {[1, 2, 3, 4, 5].map((s) => (
                                <Star key={s} size={14} className="fill-[#D4AF37] text-[#D4AF37]" />
                            ))}
                        </div>

                        {/* Quote */}
                        <blockquote className="text-[#334155] text-sm leading-relaxed italic mb-5">
                            &ldquo;{isAr ? t.quoteAr : t.quoteHe}&rdquo;
                        </blockquote>

                        {/* Author */}
                        <div className="flex items-center gap-3 pt-4 border-t border-[#F1E4D2]">
                            <img
                                src={t.photo}
                                alt={isAr ? t.nameAr : t.nameHe}
                                loading="lazy"
                                className="w-12 h-12 rounded-full object-cover ring-2 ring-[#D4AF37]/40"
                            />
                            <div>
                                <div className="font-bold text-[#0B1F3D] text-sm leading-tight">
                                    {isAr ? t.nameAr : t.nameHe}
                                </div>
                                <div className="text-[#B36318] text-[11px] font-semibold mt-0.5">
                                    {isAr ? t.roleAr : t.roleHe}
                                </div>
                                <div className="text-[#94A3B8] text-[10px] mt-0.5">
                                    {isAr ? `دفعة ${t.year}` : `מחזור ${t.year}`}
                                </div>
                            </div>
                        </div>
                    </article>
                ))}
            </div>
        </section>
    );
}
